import * as THREE from 'three';
import type { Mood } from '../../render/mood';
import type { Quality } from '../../render/post';
import { hash01 } from './meshBuilder';

/**
 * The sky behind the skyline: one full-screen triangle pinned at the far plane, drawn after the opaque
 * world so the depth test leaves it only the pixels nothing else covers (a sliver at the top of the
 * gameplay camera's 60° look-down, more from the viewpoints and the overview). One draw call, no texture
 * but a 64 px tile of value noise for the cloud mottling, no extra pass.
 *
 * The shader shades by view direction: the horizon (and everything under it) is *the fog colour*, so the
 * fully fogged ground, the skyline cards' hazed base and the sky meet without a seam; above it the sky
 * grades to the mood's zenith through a haze curve (thick near the horizon, London-style), with the
 * mood's glow band low over the horizon toward the sun: sodium-orange into violet at dusk, the faint
 * warm brightening of the sun through the cloud deck by day, the town's light pollution all round at
 * night. The band is zero at the horizon itself (the seam stays exact) and peaks a few degrees up, where
 * the skyline's silhouettes stand against it. The clouds are two octaves of the noise tile projected
 * onto a flat deck, drifting slowly; they fade out at the horizon too. All of it reads the blended
 * mood (render/mood.ts `dome`), so cross-fades carry through, and the fog colour every frame.
 *
 * LOW draws the gradient and the glow without the clouds.
 */
export interface SkyDome {
  readonly mesh: THREE.Mesh;
  setMood(m: Mood): void;
  setTier(t: Quality): void;
  update(time: number): void;
  dispose(): void;
}

/** Noise tile edge (px). */
export const CLOUD_TILE = 64;

/**
 * A tileable value-noise tile (three octaves, lattices of 4, 8 and 16 cells) as RGBA8 bytes, R = the
 * noise, 0..255; G, B, A unused. Deterministic (hash01), ~16 kB.
 */
export function cloudTile(size = CLOUD_TILE, seed = 7): Uint8Array {
  const out = new Uint8Array(size * size * 4);
  const octaves: [number, number][] = [
    [4, 0.55],
    [8, 0.3],
    [16, 0.15],
  ];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let v = 0;
      for (const [cells, amp] of octaves) {
        const fx = (x / size) * cells;
        const fy = (y / size) * cells;
        const x0 = Math.floor(fx);
        const y0 = Math.floor(fy);
        const tx = fx - x0;
        const ty = fy - y0;
        const sx = tx * tx * (3 - 2 * tx);
        const sy = ty * ty * (3 - 2 * ty);
        // lattice values wrap, so the tile does
        const at = (ix: number, iy: number): number => hash01(((ix % cells) + cells) % cells, ((iy % cells) + cells) % cells, seed + cells);
        const a = at(x0, y0);
        const b = at(x0 + 1, y0);
        const c = at(x0, y0 + 1);
        const d = at(x0 + 1, y0 + 1);
        v += amp * ((a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy);
      }
      const i = (y * size + x) * 4;
      out[i] = Math.round(Math.min(1, Math.max(0, v)) * 255);
      out[i + 1] = out[i + 2] = 0;
      out[i + 3] = 255;
    }
  }
  return out;
}

/** The full-screen triangle (clip-space corners; the vertex shader uses xy as is). */
export function fullScreenTriangle(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
  return geo;
}

const VERT = /* glsl */ `
uniform mat4 uInvProj;
varying vec3 vDir;
void main() {
  // the far-plane point of this corner, in view space, then into the world: linear in the clip corner,
  // so it interpolates straight across the triangle (normalised per pixel)
  vec4 p = uInvProj * vec4(position.xy, 1.0, 1.0);
  vDir = (p.xyz / p.w) * mat3(viewMatrix);
  // just inside the far plane (the skyline cards pin themselves a step nearer)
  gl_Position = vec4(position.xy, 0.999999, 1.0);
}`;

const FRAG = /* glsl */ `
uniform vec3 uHorizon;
uniform vec3 uZenith;
uniform vec3 uGlow;
uniform vec3 uGlowHigh;
uniform float uGlowLevel;
uniform float uGlowFocus;
uniform float uGlowSpread;
uniform vec2 uGlowDir;
uniform float uCloud;
uniform sampler2D uNoise;
uniform float uTime;
varying vec3 vDir;
void main() {
  vec3 d = normalize(vDir);
  float h = max(d.y, 0.0);
  // the haze is thick at the horizon: the zenith only shows well overhead
  vec3 c = mix(uHorizon, uZenith, 1.0 - exp(-h * 3.0));
  // the glow band: none on the horizon line itself, a peak a few degrees up, gone by ~30 degrees
  float band = smoothstep(0.0, 0.035, h) * exp(-h * 7.0);
  vec2 flat2 = normalize(d.xz + vec2(1.0e-5, 0.0));
  float az = dot(flat2, uGlowDir) * 0.5 + 0.5;
  float toward = mix(1.0, pow(az, uGlowFocus), uGlowSpread);
  vec3 gc = mix(uGlow, uGlowHigh, smoothstep(0.03, 0.25, h));
  c += gc * (band * toward * uGlowLevel);
  if (uCloud > 0.0) {
    // a flat cloud deck seen in perspective: the tile stretches into bands toward the horizon
    vec2 cp = d.xz / (h + 0.12);
    float n = texture2D(uNoise, cp * vec2(0.035, 0.06) + uTime * vec2(0.0016, 0.0005)).r;
    n = n * 0.65 + texture2D(uNoise, cp * vec2(0.11, 0.17) + uTime * vec2(0.004, 0.001)).r * 0.35;
    c *= 1.0 + (n - 0.5) * uCloud * smoothstep(0.012, 0.09, h);
  }
  gl_FragColor = vec4(c, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

export function createSkyDome(scene: THREE.Scene): SkyDome {
  const noise = new THREE.DataTexture(cloudTile(), CLOUD_TILE, CLOUD_TILE, THREE.RGBAFormat);
  noise.wrapS = noise.wrapT = THREE.RepeatWrapping;
  noise.magFilter = noise.minFilter = THREE.LinearFilter;
  noise.generateMipmaps = false;
  noise.needsUpdate = true;
  const uniforms = {
    uInvProj: { value: new THREE.Matrix4() },
    uHorizon: { value: new THREE.Color(0x232a36) },
    uZenith: { value: new THREE.Color(0x151a28) },
    uGlow: { value: new THREE.Color(0) },
    uGlowHigh: { value: new THREE.Color(0) },
    uGlowLevel: { value: 0 },
    uGlowFocus: { value: 4 },
    uGlowSpread: { value: 1 },
    uGlowDir: { value: new THREE.Vector2(-0.83, 0.55) },
    uCloud: { value: 0 },
    uNoise: { value: noise },
    uTime: { value: 0 },
  };
  const mat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: VERT,
    fragmentShader: FRAG,
    depthWrite: false,
    depthTest: true,
  });
  const mesh = new THREE.Mesh(fullScreenTriangle(), mat);
  mesh.name = 'sky';
  mesh.matrixAutoUpdate = false;
  mesh.frustumCulled = false;
  // opaque, but after the rest of the opaque world: the depth test then skips every covered pixel
  mesh.renderOrder = 1;
  mesh.onBeforeRender = (_r, _s, camera) => {
    uniforms.uInvProj.value.copy(camera.projectionMatrixInverse);
  };
  let cloud = 0;
  let tier: Quality = 'high';
  const applyCloud = (): void => {
    uniforms.uCloud.value = tier === 'low' ? 0 : cloud;
  };
  return {
    mesh,
    setMood(m) {
      const d = m.dome;
      uniforms.uZenith.value.setHex(d.zenith);
      uniforms.uGlow.value.setHex(d.glow);
      uniforms.uGlowHigh.value.setHex(d.glowHigh);
      uniforms.uGlowLevel.value = d.glowLevel;
      uniforms.uGlowFocus.value = d.glowFocus;
      uniforms.uGlowSpread.value = d.glowSpread;
      uniforms.uGlowDir.value.set(d.glowDir[0], d.glowDir[1]).normalize();
      cloud = d.cloud;
      applyCloud();
    },
    setTier(t) {
      tier = t;
      applyCloud();
    },
    update(time) {
      uniforms.uTime.value = time;
      const fog = scene.fog as THREE.FogExp2 | null;
      if (fog) uniforms.uHorizon.value.copy(fog.color);
    },
    dispose() {
      mesh.geometry.dispose();
      mat.dispose();
      noise.dispose();
    },
  };
}
